import {
  Alert,
  Button,
  Modal,
  Radio,
  Skeleton,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  MergeCellsOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import React, { useEffect, useMemo, useState } from 'react';
import {
  CandidateMergeFieldSource,
  CandidateMergePreview,
  CandidateMergeResult,
  CandidateMergeSelections,
  CandidateMergeSnapshot,
} from '../models/candidate-merge.model';
import { CandidateMergeService } from '../services/candidate-merge.service';
import './CandidateMergeModal.css';

interface Props {
  open: boolean;
  candidateIds: string[];
  onCancel: () => void;
  onMerged: (result: CandidateMergeResult) => void;
}

const initialSelections = (): CandidateMergeSelections => ({
  firstname: 'PRIMARY',
  lastname: 'PRIMARY',
  email: 'PRIMARY',
  phoneNumber: 'PRIMARY',
  profileURL: 'PRIMARY',
  rate: 'PRIMARY',
});

const fullName = (candidate: CandidateMergeSnapshot) =>
  `${candidate.firstname || ''} ${candidate.lastname || ''}`.trim();

const display = (value: string | number | null | undefined) =>
  value === null || value === undefined || value === ''
    ? 'Not provided'
    : String(value);

const CandidateMergeModal: React.FC<Props> = ({
  open,
  candidateIds,
  onCancel,
  onMerged,
}) => {
  const [preview, setPreview] = useState<CandidateMergePreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [merging, setMerging] = useState(false);
  const [primaryId, setPrimaryId] = useState('');
  const [selections, setSelections] =
    useState<CandidateMergeSelections>(initialSelections);

  useEffect(() => {
    if (!open || candidateIds.length !== 2) return;
    setLoading(true);
    setPreview(null);
    setPrimaryId(candidateIds[0]);
    setSelections(initialSelections());
    void CandidateMergeService.preview(candidateIds[0], candidateIds[1])
      .then(setPreview)
      .catch(() => message.error('Could not compare these candidates.'))
      .finally(() => setLoading(false));
  }, [candidateIds, open]);

  const ordered = useMemo(() => {
    if (!preview) return null;
    return primaryId === preview.first.id
      ? { primary: preview.first, source: preview.second }
      : { primary: preview.second, source: preview.first };
  }, [preview, primaryId]);

  const selectPrimary = (id: string) => {
    setPrimaryId(id);
    setSelections(initialSelections());
  };

  const choose = (
    field: keyof CandidateMergeSelections,
    value: CandidateMergeFieldSource
  ) => setSelections((current) => ({ ...current, [field]: value }));

  const merge = async () => {
    if (!ordered) return;
    setMerging(true);
    try {
      const result = await CandidateMergeService.merge(
        ordered.primary.id,
        ordered.source.id,
        selections
      );
      message.success('Candidates merged successfully.');
      onMerged(result);
    } catch {
      message.error(
        'The candidates could not be merged. No records were changed.'
      );
    } finally {
      setMerging(false);
    }
  };

  const fields: Array<{
    key: keyof CandidateMergeSelections;
    label: string;
    value: (candidate: CandidateMergeSnapshot) => string | number | null;
  }> = [
    {
      key: 'firstname',
      label: 'First name',
      value: (candidate) => candidate.firstname,
    },
    {
      key: 'lastname',
      label: 'Last name',
      value: (candidate) => candidate.lastname,
    },
    {
      key: 'email',
      label: 'Primary email',
      value: (candidate) => candidate.email,
    },
    {
      key: 'phoneNumber',
      label: 'Phone',
      value: (candidate) => candidate.phoneNumber,
    },
    {
      key: 'profileURL',
      label: 'Profile URL',
      value: (candidate) => candidate.profileURL,
    },
    { key: 'rate', label: 'Rating', value: (candidate) => candidate.rate },
  ];

  return (
    <Modal
      open={open}
      title={
        <span className="candidate-merge__title">
          <MergeCellsOutlined /> Merge duplicate candidates
        </span>
      }
      onCancel={onCancel}
      width={860}
      destroyOnHidden
      footer={
        <>
          <Button onClick={onCancel} disabled={merging}>
            Cancel
          </Button>
          <Button
            type="primary"
            loading={merging}
            disabled={!ordered}
            onClick={() => void merge()}
          >
            Merge into primary candidate
          </Button>
        </>
      }
    >
      {loading ? (
        <div role="status" aria-label="Loading candidate comparison">
          <Skeleton active paragraph={{ rows: 8 }} />
        </div>
      ) : ordered && preview ? (
        <div className="candidate-merge">
          <Alert
            type="warning"
            showIcon
            message="This action combines records"
            description="Applications and their pipeline history remain unchanged. The secondary record will be archived and redirect to the primary candidate."
          />

          <section aria-labelledby="candidate-merge-primary">
            <Typography.Title level={5} id="candidate-merge-primary">
              Choose the primary record
            </Typography.Title>
            <Radio.Group
              value={primaryId}
              onChange={(event) => selectPrimary(event.target.value)}
            >
              {[preview.first, preview.second].map((candidate) => (
                <Radio.Button key={candidate.id} value={candidate.id}>
                  {fullName(candidate)} · {candidate.email}
                </Radio.Button>
              ))}
            </Radio.Group>
          </section>

          <section
            className="candidate-merge__counts"
            aria-label="Records to preserve"
          >
            <div>
              <strong>
                {ordered.primary.applicationCount +
                  ordered.source.applicationCount}
              </strong>
              <span>Applications</span>
            </div>
            <div>
              <strong>
                {ordered.primary.fileCount + ordered.source.fileCount}
              </strong>
              <span>Files before deduplication</span>
            </div>
            <div>
              <strong>
                {ordered.primary.skillCount + ordered.source.skillCount}
              </strong>
              <span>Skills before deduplication</span>
            </div>
          </section>

          <section aria-labelledby="candidate-merge-fields">
            <div className="candidate-merge__section-heading">
              <Typography.Title level={5} id="candidate-merge-fields">
                Choose the surviving profile values
              </Typography.Title>
              <Tag icon={<SafetyCertificateOutlined />} color="blue">
                Pipeline-safe
              </Tag>
            </div>
            <div className="candidate-merge__field-grid">
              <strong>Field</strong>
              <strong>Primary record</strong>
              <strong>Secondary record</strong>
              {fields.map((field) => (
                <React.Fragment key={field.key}>
                  <span>{field.label}</span>
                  <Radio
                    checked={selections[field.key] === 'PRIMARY'}
                    onChange={() => choose(field.key, 'PRIMARY')}
                  >
                    {display(field.value(ordered.primary))}
                  </Radio>
                  <Radio
                    checked={selections[field.key] === 'SOURCE'}
                    onChange={() => choose(field.key, 'SOURCE')}
                  >
                    {display(field.value(ordered.source))}
                  </Radio>
                </React.Fragment>
              ))}
            </div>
          </section>

          <Typography.Paragraph
            type="secondary"
            className="candidate-merge__email-note"
          >
            The email not selected as primary will be preserved as an alternate
            email for duplicate detection and audit history.
          </Typography.Paragraph>
        </div>
      ) : (
        <Alert
          type="error"
          showIcon
          message="Candidate comparison is unavailable"
        />
      )}
    </Modal>
  );
};

export default CandidateMergeModal;
